import React, { useCallback, useLayoutEffect, useMemo } from 'react'
import Badge from 'react-bootstrap/Badge'
import Card from 'react-bootstrap/Card'
import CardHeader from 'react-bootstrap/CardHeader'
import Container from 'react-bootstrap/Container'
import Col from 'react-bootstrap/Col'
import Row from 'react-bootstrap/Row'
import Button from 'react-bootstrap/Button'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import {
    useNavigate,
    useParams,
    generatePath,
    useSearchParams,
} from 'react-router-dom'
import { RootState } from 'app/state/types'
import { setApp, setIsLoading, setApks } from 'app/state/appSlice'
import {
    getApp,
    getAppByPackageName,
    getAppApks,
    getAppIconUrl,
    getAppDownloadUrl,
} from 'app/services/client'
import VersionsView from './VersionsView'
import { APP_ROUTES } from 'app/components/AppRouter/routes'
import { isTokenExpired, makeDownloadApkLink } from 'app/utils/hooks'
import { Versions } from 'domain/AppInfo'
import { handleServerAPIError } from 'app/services/utils'
import { onImageLoadError } from 'app/utils/fileUtils'
import hotspotCircleGreen from 'assets/images/hotspot_circle_green.svg'
import hotspotCircle from 'assets/images/hotspot_circle.svg'
import pencil from 'assets/images/pencil.svg'
import plus from 'assets/images/plus.svg'
import { SizableImage } from 'app/components/Image'

const AppLogo = styled.img`
    width: 160px;
    border-radius: 37px;
`

const AppDetailsView = () => {
    const { app, apks, appCategories } = useSelector(
        (state: RootState) => state.appSlice,
    )
    const { cdnInfo } = useSelector((state: RootState) => state.generalSlice)
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const { id } = useParams<{
        id: string
    }>()
    const [searchParams] = useSearchParams()

    useLayoutEffect(() => {
        if (id && id !== 'show') {
            dispatch(setIsLoading(true))
            getApp(id)
                .then(res => {
                    if (!res.data.approved && isTokenExpired()) {
                        navigate(APP_ROUTES.PAGE404_ERROR)
                        dispatch(setIsLoading(false))
                    } else {
                        dispatch(setApp(res.data))

                        getAppApks(res.data.id.toString())
                            .then(resApks => {
                                dispatch(setApks(resApks.data))
                            })
                            .catch(e =>
                                handleServerAPIError(e, dispatch, navigate),
                            )
                            .finally(() => dispatch(setIsLoading(false)))
                    }
                })
                .catch(e => handleServerAPIError(e, dispatch, navigate))
        } else if (id && id === 'show') {
            const packageName = searchParams.get('package_name')

            if (packageName) {
                dispatch(setIsLoading(true))
                getAppByPackageName(packageName)
                    .then(res => {
                        dispatch(setApp(res.data))

                        getAppApks(res.data.id.toString())
                            .then(resApks => {
                                dispatch(setApks(resApks.data))
                            })
                            .catch(e =>
                                handleServerAPIError(e, dispatch, navigate),
                            )
                            .finally(() => dispatch(setIsLoading(false)))
                    })
                    .catch(e => handleServerAPIError(e, dispatch, navigate))
            } else {
                navigate(APP_ROUTES.ROOT)
            }
        }
    }, [])

    const findLastVersion = useMemo(() => {
        if (apks.length === 0) return {} as Versions

        const found = apks
            .filter(a => a.uuid)
            .sort((a1, a2) => a2.versionCode - a1.versionCode)

        return found[0]
    }, [apks])

    const getCategoryName = useCallback(() => {
        const categoryId = app.appCategoryLookupId
        return appCategories.find(cat => cat.id === categoryId)?.appCategoryName
    }, [appCategories, app])

    const getAppImageUrl = useCallback(() => {
        if (cdnInfo.imageCdn.enable && cdnInfo.imageCdn.url !== undefined) {
            const cdnLink = cdnInfo.imageCdn.url
            const cleanCdnLink =
                cdnLink.charAt(cdnLink.length - 1) === '/'
                    ? cdnLink.slice(0, -1)
                    : cdnLink
            return getAppIconUrl(app.id, cleanCdnLink)
        } else {
            return getAppIconUrl(app.id)
        }
    }, [cdnInfo, app.id])

    const getAppDownloadLink = useCallback(() => {
        let result = ''

        if (findLastVersion.id === undefined || app.id === undefined)
            return result

        if (
            cdnInfo.applicationCdn.enable &&
            cdnInfo.applicationCdn.url !== undefined
        ) {
            const cdnLink = cdnInfo.applicationCdn.url
            const cleanCdnLink =
                cdnLink.charAt(cdnLink.length - 1) === '/'
                    ? cdnLink.slice(0, -1)
                    : cdnLink

            result = getAppDownloadUrl(
                app.id.toString(),
                findLastVersion.id.toString(),
                cleanCdnLink,
            )
        } else {
            result = getAppDownloadUrl(
                app.id.toString(),
                findLastVersion.id.toString(),
            )
        }
        return result
    }, [cdnInfo, app.id, findLastVersion])

    return (
        <Container>
            <Col xs="12" className="mt-2 mb-2">
                <Row className="d-flex">
                    <Col xs="auto">
                        <Row>
                            <Col xs="auto">
                                <h1 className="app-name fw-bold">
                                    {app.appName}
                                </h1>
                            </Col>
                            {!isTokenExpired() && (
                                <Col xs="auto">
                                    <Button
                                        variant="primary"
                                        onClick={() =>
                                            navigate(
                                                APP_ROUTES.EDIT_APP.replace(
                                                    ':id',
                                                    app.id.toString(),
                                                ),
                                            )
                                        }>
                                        <SizableImage
                                            url={pencil}
                                            size="24px"
                                        />
                                    </Button>
                                </Col>
                            )}
                        </Row>
                        <Row>
                            <h3 className="app-package-name fw-light lh-1">{`Package Name: ${app.packageName}`}</h3>
                        </Row>
                        <Row>
                            <h3 className="app-category fw-light  lh-1">
                                Category: {getCategoryName() || ''}
                            </h3>
                        </Row>
                        <Row xs="auto">
                            <Badge
                                className="app-version mb-2"
                                bg="secondary"
                                style={{ marginLeft: '12px' }}>
                                Ver:{' '}
                                {apks.length > 0 &&
                                    findLastVersion?.versionName}{' '}
                                {apks.length > 0 &&
                                    findLastVersion?.versionCode &&
                                    '(' + findLastVersion?.versionCode + ')'}
                            </Badge>
                        </Row>
                    </Col>
                    <Col
                        className="ms-auto"
                        xs="auto"
                        style={{
                            alignItems: 'center',
                            alignContent: 'center',
                            alignSelf: 'center',
                        }}>
                        <Row>
                            <AppLogo
                                className="app-icon"
                                src={getAppImageUrl()}
                                onError={onImageLoadError}
                            />
                        </Row>
                        {!isTokenExpired() && (
                            <Row
                                className="d-flex"
                                style={{ marginRight: '15px' }}>
                                {app.approved ? (
                                    <SizableImage
                                        className="p-0 ms-auto"
                                        url={hotspotCircleGreen}
                                        size="35px"
                                    />
                                ) : (
                                    <SizableImage
                                        className="p-0"
                                        url={hotspotCircle}
                                        size="35px"
                                    />
                                )}
                            </Row>
                        )}
                    </Col>
                </Row>
                {app.appDescription && (
                    <Card>
                        <CardHeader>
                            <Card.Title>{'Description'}</Card.Title>
                        </CardHeader>
                        <Card.Body>{app.appDescription}</Card.Body>
                    </Card>
                )}
                <Row className="align-items-center text-center d-flex mb-2">
                    <Col xs="auto">
                        <h2 className="mb-0">{'APKS'}</h2>
                    </Col>
                    {!isTokenExpired() && (
                        <Col xs="auto">
                            <Button
                                variant="success"
                                onClick={() =>
                                    navigate(
                                        generatePath(APP_ROUTES.ADD_APK, {
                                            appid: id,
                                        }),
                                    )
                                }>
                                <SizableImage url={plus} size="20px" />
                            </Button>
                        </Col>
                    )}
                    {!isTokenExpired() && (
                        <Col className="ms-auto" xs="auto">
                            {apks.length === 0 ||
                            !findLastVersion?.apkUploaded ? (
                                <Button
                                    className="app-link"
                                    disabled={
                                        apks.length === 0 ||
                                        !findLastVersion?.apkUploaded
                                    }
                                    variant="success"
                                    onClick={() => {
                                        if (findLastVersion)
                                            window.open(
                                                makeDownloadApkLink(
                                                    findLastVersion.appId.toString(),
                                                    findLastVersion.id.toString(),
                                                ),
                                                '_blank',
                                            )
                                    }}>
                                    DOWNLOAD LATEST VERSION
                                </Button>
                            ) : (
                                <Button
                                    className="app-link"
                                    href={getAppDownloadLink()}
                                    disabled={
                                        apks.length === 0 ||
                                        !findLastVersion?.apkUploaded
                                    }
                                    variant="success">
                                    DOWNLOAD LATEST VERSION
                                </Button>
                            )}
                        </Col>
                    )}
                </Row>
                <VersionsView />
            </Col>
        </Container>
    )
}

export default AppDetailsView
